<template>
	<template v-if="visible && refs.items.length > 0 && (hasVisibleRefs || canAddCount>0)">
		<div class="fd-widget-group references-group" :class="groupClass" :style="style" ref="container"><div class="reset-gutter">
			<template v-for="(ref, i) in refs.items">
				<reference-part v-if="evalRefs[i].visible" :data="localData" :part="ref" :readonly="ro" :viewmode="vm" :index="i" @hide="indexRemoved(i)"></reference-part>
			</template>
			<div v-if="canAddCount > 0" class="reference-group-controls pos-wide">
				<anchor-element tag="span" :chain="chain" @click="chain.toggle" class="ref-add-menu-label"><sym-icon name="link-l" class="ref-add-icon"></sym-icon><span>{{ env.msg('menu.addRelation') }}</span></anchor-element>
				<boat-element tag="div" :chain="chain" class="menu-panel">
					<div class="menu-panel-body">
						<template v-for="(ref, i) in refs.items">
							<a href="#" v-if="!evalRefs[i].visible" class="menu-item" @click.prevent="addRefIndex(i)">{{ ref.label }}</a>
						</template>
					</div>
				</boat-element>
			</div>
		</div></div>
	</template>
</template>

<script setup lang="ts">
import {ref, computed, nextTick} from "vue";
import useContainer, { type ContainerProps } from "@fd/containers/Container.ts";
import {References, Reference} from "@t/fd/editor/References.ts";
import {injectMaskingLib} from "@fd/Masking.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree.ts";
import {injectEnv} from "@fd/Environment.ts";
import ReferencePart from "@fd/editors/reference/ReferencePart.vue";

const props = defineProps<ContainerProps>();
const {visible, componentOfPart, partRef, localData, ro, vm, clazz, style} = useContainer(props);
const refs = computed<References>(() => props.part as References);
const env = injectEnv();

/** Indexes of references which are already added but not yet pass empty-test */
const addedIndexes = ref<number[]>([]);
const mlib = injectMaskingLib();
const container = ref<HTMLDivElement|null>(null);

const chain = createChain({
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: 0, y: 0
	}
}, "references-add-menu");

interface EvaluatedRef {
	index: number;
	empty: boolean;
	visible: boolean;
}

function indexAdded(idx:number) {
	return addedIndexes.value.indexOf(idx) >= 0;
}

function indexRemoved(idx:number) {
	const i = addedIndexes.value.indexOf(idx);
	if (i >= 0) addedIndexes.value.splice(i, 1);
}

/**
 * This method assumes that Nth element will contain suggester with .fd-editable inside it
 */
function focusIndex(idx: number) {
	nextTick(() => {
		if (container.value) {
			const editorAtIdx = container.value.querySelector("[data-index=\""+idx+"\"]");
			if (editorAtIdx) {
				const focusElement = editorAtIdx.querySelector(".fd-editable") as HTMLElement;
				if (focusElement) {
					focusElement.focus();
				}
			}
		}
	});
}

function addRefIndex(idx:number) {
	addedIndexes.value.push(idx);
	chain.hide();
	focusIndex(idx);
}

const evalRefs = computed<EvaluatedRef[]>(() => {
	const e:EvaluatedRef[] = [];
	let index = 0;
	for (let r of refs.value.items) {
		let empty;
		if (r.test) empty = mlib.evaluateTest(localData.value, vm.value, r.test);
		else empty = false;
		e.push({ index, empty, visible: (!empty || indexAdded(index) || !!r.keep) });
		index++;
	}
	return e;
});

const hasVisibleRefs = computed<boolean>(() => {
	for (let er of evalRefs.value) {
		if (er.visible) return true;
	}
	return false;
});

const canAddCount = computed<number>(() => {
	if (ro.value) return 0;
	let count = 0;
	for (let er of evalRefs.value) {
		if (!er.visible) count++;
	}
	return count;
});

const groupClass = computed<string[]>(() => {
	let gcs:string[] = ['wg-separ-color-sided']
	let cz = clazz.value;
	if (Array.isArray(cz)) gcs.push(...cz);
	return gcs;
});

</script>

<style>
.ref-add-menu-label {
	color: var(--panel-link-fg);
	cursor: context-menu;
	display: flex;			/* This is in sync with .ref-header-row to align well */
	flex-direction: row;
	column-gap: 4px;

	&:hover {
		color: var(--panel-link-fg-hover);
	}

	& > .ref-add-icon {
		text-align: center;			/* This is in sync with .ref-header-icon to align well */
		width: 20px;
	}
}

.ref-add-menu {
	background-color: var(--form-sbg);
	border: 1px solid var(--form-border-color);
	border-radius: var(--menu-radius);
	padding: var(--menu-radius);
	color: var(--form-fg);
	box-shadow: var(--panel-shadow);

	display: flex;
	flex-direction: column;
	line-height: var(--input-height);
}
.ref-add-menu-item {

}
</style>